import { prefersReducedMotion } from '@nulo/a11y';
import { createSwiper } from '@nulo/swiper';

const HERO_PAGINATION_BREAKPOINT = 768;

/**
 * @typedef {Object} HeroSliderI18n
 * @property {string} [pause]
 * @property {string} [play]
 * @property {string} [slideOf]
 * @property {string} [slideNamed]
 * @property {string} [slideNumber]
 * @property {string} [prevSlide]
 * @property {string} [nextSlide]
 */

/**
 * @param {string} template
 * @param {Record<string, string | number>} vars
 */
function interpolate(template, vars) {
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => String(vars[key] ?? ''));
}

/**
 * @extends {HTMLElement}
 */
class HeroSlider extends HTMLElement {
  /** @type {import('swiper').Swiper | null} */
  #swiper = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {boolean} */
  #playing = true;

  /** @type {HeroSliderI18n} */
  #i18n = {};

  /**
   * @returns {HeroSliderI18n}
   */
  #parseI18n() {
    return {
      pause: this.dataset.i18nPause || '',
      play: this.dataset.i18nPlay || '',
      slideOf: this.dataset.i18nSlideOf || '',
      slideNamed: this.dataset.i18nSlideNamed || '',
      slideNumber: this.dataset.i18nSlideNumber || '',
      prevSlide: this.dataset.i18nPrevSlide || '',
      nextSlide: this.dataset.i18nNextSlide || '',
    };
  }

  connectedCallback() {
    if (this.#swiper) return;

    this.#i18n = this.#parseI18n();

    const container = this.querySelector('[data-swiper]');
    if (!(container instanceof HTMLElement)) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const reducedMotion = prefersReducedMotion();
    const autoplayDelay = parseInt(this.dataset.autoplayDelay || '5000', 10);
    const paginationEl = container.querySelector('.hero-slider__pagination');
    const prevEl = container.querySelector('.hero-slider__nav--prev');
    const nextEl = container.querySelector('.hero-slider__nav--next');
    const playPauseBtn = this.querySelector('.hero-slider__play-pause');

    this.#playing = !reducedMotion;
    const progressMs = autoplayDelay + 50;

    this.style.setProperty('--hero-autoplay-ms', `${progressMs}ms`);
    container.style.setProperty('--hero-autoplay-ms', `${progressMs}ms`);

    if (paginationEl instanceof HTMLElement) {
      paginationEl.addEventListener(
        'click',
        (event) => {
          if (!this.#isDesktopPagination()) return;

          const target = event.target;
          if (!(target instanceof Element)) return;

          const bullet = target.closest('li.swiper-pagination-bullet');
          if (!(bullet instanceof HTMLElement) || !paginationEl.contains(bullet)) return;

          paginationEl.querySelectorAll('li.active').forEach((item) => {
            item.classList.remove('active');
          });
          bullet.classList.add('active');

          if (this.#playing) {
            this.#playing = false;
            this.#setPaused(true, container, playPauseBtn, this.#swiper, paginationEl);
          }
        },
        { signal }
      );
    }

    /** @type {import('swiper').PaginationOptions | false} */
    const paginationConfig = paginationEl
      ? {
          el: paginationEl,
          enabled: false,
          clickable: true,
          renderBullet: (index, className) => {
            const slides = container.querySelectorAll('.swiper-slide');
            const slide = slides[index];
            const title = slide?.querySelector('.hero-slider__title')?.textContent?.trim() || '';
            const slideIndex = index + 1;
            const ariaLabel = title
              ? interpolate(this.#i18n.slideNamed || 'Slide {{index}}: {{title}}', {
                  index: slideIndex,
                  title,
                })
              : interpolate(this.#i18n.slideNumber || 'Slide {{index}}', { index: slideIndex });
            const displayTitle =
              title ||
              interpolate(this.#i18n.slideNumber || 'Slide {{index}}', { index: slideIndex });

            return `<li role="listitem" class="${className}"><button type="button" aria-label="${ariaLabel}"><span>${displayTitle}</span></button></li>`;
          },
          bulletClass: 'swiper-pagination-bullet',
          bulletActiveClass: 'swiper-pagination-bullet-active',
        }
      : false;

    this.#swiper = createSwiper({
      region: this,
      el: container,
      activeSlideClass: 'swiper-slide-active',
      config: {
        loop: true,
        slidesPerView: 1,
        slidesPerGroup: 1,
        speed: 500,
        a11y: {
          enabled: true,
          slideLabelMessage: this.#i18n.slideOf || 'Slide {{index}} of {{slidesLength}}',
          prevSlideMessage: this.#i18n.prevSlide || 'Previous slide',
          nextSlideMessage: this.#i18n.nextSlide || 'Next slide',
        },
        pagination: paginationConfig,
        breakpoints: paginationConfig
          ? {
              [HERO_PAGINATION_BREAKPOINT]: {
                pagination: {
                  enabled: true,
                },
              },
            }
          : undefined,
        navigation:
          prevEl && nextEl
            ? {
                prevEl,
                nextEl,
              }
            : false,
        autoplay: reducedMotion
          ? false
          : {
              delay: autoplayDelay,
              disableOnInteraction: false,
            },
        on: {
          init: (sw) => {
            this.#syncHeroPagination(sw, container, paginationEl, reducedMotion);
          },
          breakpoint: (sw) => {
            this.#syncHeroPagination(sw, container, paginationEl, reducedMotion);
          },
          slideChange: (sw) => {
            if (!this.#isDesktopPagination()) return;

            this.#updateHeroPaginationAria(sw);
            if (!container.classList.contains('paused')) {
              this.#clearPaginationActiveStates(paginationEl);
            }
          },
          slideChangeTransitionEnd: () => {
            if (
              this.#isDesktopPagination() &&
              !container.classList.contains('paused')
            ) {
              this.#restartPaginationProgress(paginationEl);
            }
          },
          paginationUpdate: (sw) => {
            if (!this.#isDesktopPagination()) return;
            this.#updateHeroPaginationAria(sw);
          },
        },
      },
    });

    if (!this.#swiper) return;

    if (reducedMotion) {
      this.#setPaused(true, container, playPauseBtn, this.#swiper, paginationEl);
    }

    if (playPauseBtn instanceof HTMLButtonElement) {
      playPauseBtn.addEventListener(
        'click',
        () => {
          if (this.#playing) {
            this.#playing = false;
            this.#setPaused(true, container, playPauseBtn, this.#swiper, paginationEl);
            return;
          }

          this.#playing = true;
          this.#setPaused(false, container, playPauseBtn, this.#swiper, paginationEl);

          if (window.matchMedia(`(min-width: ${HERO_PAGINATION_BREAKPOINT}px)`).matches) {
            this.#swiper?.slideNext();
          }
        },
        { signal }
      );
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;

    this.#swiper?.destroy?.(true, true);
    this.#swiper = null;
  }

  /**
   * @returns {boolean}
   */
  #isDesktopPagination() {
    return window.matchMedia(`(min-width: ${HERO_PAGINATION_BREAKPOINT}px)`).matches;
  }

  /**
   * @param {import('swiper').Swiper} swiper
   * @param {HTMLElement} container
   * @param {Element | null | undefined} paginationEl
   * @param {boolean} reducedMotion
   */
  #syncHeroPagination(swiper, container, paginationEl, reducedMotion) {
    if (!this.#isDesktopPagination()) return;

    this.#updateHeroPaginationAria(swiper);
    if (!reducedMotion && !container.classList.contains('paused')) {
      this.#restartPaginationProgress(paginationEl);
    }
  }

  /**
   * @param {Element | null | undefined} paginationEl
   */
  #clearPaginationActiveStates(paginationEl) {
    paginationEl?.querySelectorAll('li.active').forEach((li) => li.classList.remove('active'));
  }

  /**
   * Restart the desktop pagination fill animation when the slide transition ends
   * so progress stays in sync with Swiper autoplay (delay starts after transition).
   *
   * @param {Element | null | undefined} paginationEl
   */
  #restartPaginationProgress(paginationEl) {
    const btn = paginationEl?.querySelector(
      '.swiper-pagination-bullet-active:not(.active) button'
    );
    if (!(btn instanceof HTMLElement)) return;

    btn.style.animation = 'none';
    void btn.offsetWidth;
    btn.style.removeProperty('animation');
  }

  /**
   * @param {import('swiper').Swiper} swiper
   */
  #updateHeroPaginationAria(swiper) {
    const bullets = swiper.pagination?.bullets || [];

    for (const [index, bullet] of bullets.entries()) {
      const btn = bullet instanceof HTMLElement ? bullet.querySelector('button') : null;
      if (btn instanceof HTMLElement) {
        btn.setAttribute('aria-current', index === swiper.realIndex ? 'true' : 'false');
      }
    }
  }

  /**
   * @param {boolean} paused
   * @param {HTMLElement} container
   * @param {Element | null} playPauseBtn
   * @param {import('swiper').Swiper | null} swiper
   * @param {Element | null | undefined} paginationEl
   */
  #setPaused(paused, container, playPauseBtn, swiper, paginationEl) {
    container.classList.toggle('paused', paused);

    if (paused) {
      swiper?.autoplay?.stop();
    } else if (this.#isDesktopPagination()) {
      this.#clearPaginationActiveStates(paginationEl);
      swiper?.autoplay?.start();
      this.#restartPaginationProgress(paginationEl);
    }

    if (!(playPauseBtn instanceof HTMLButtonElement)) return;

    playPauseBtn.setAttribute('aria-label', paused ? this.#i18n.play || '' : this.#i18n.pause || '');
    playPauseBtn.dataset.state = paused ? 'paused' : 'playing';

    const iconPlay = playPauseBtn.querySelector('.hero-slider__icon-play');
    const iconPause = playPauseBtn.querySelector('.hero-slider__icon-pause');

    if (iconPlay instanceof HTMLElement) iconPlay.hidden = !paused;
    if (iconPause instanceof HTMLElement) iconPause.hidden = paused;
  }
}

if (!customElements.get('hero-slider')) {
  customElements.define('hero-slider', HeroSlider);
}
